// Disposition: Touren in Merxo (docs/integration/bestell-app.md §18). Merxo führt die Tour – Tag, Fahrzeug, Fahrer, Reihenfolge
// der Stopps, geplanter Beginn, Route und geplante Ankunftszeiten; die Bestell-App führt sie aus (Start, „Ich bin da“,
// Zustellung, Ende, Live-Ortung) und meldet zurück. Hier: die Verträge der Oberfläche (sales.tour.*, Rechte
// sales.tour.read | write), die Ereignisse und die öffentlichen Verträge für die App (sales.channel.tours | reportTour |
// reportPositions, Rechte sales.tour.read | report).
//
// KI-Assistent (§18.4): lesende Verträge 'default', schreibende 'opt-in' (nur nach Freigabe durch den Betrieb), die der App
// 'never'.
//
// Zeitpunkte als z.date() (Oberfläche und JSON: ISO 8601), Eingaben von Zeitpunkten als ISO-Text; Kalendertage 'YYYY-MM-DD'
// (Europe/Berlin), Uhrzeiten 'HH:MM'. Ladung in Kasten-Äquivalenten (Dezimalzahl, zwei Nachkommastellen).
import { defineContract, defineEvent } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'
import { GEOCODE_SOURCES, STOP_STATUSES, TOUR_STATUSES, TOUR_WARNING_CODES } from '../lib/labels'
import { zDay, zTime } from './common'
import { STOP_KINDS } from '../lib/events'
import { zChannelEventStop } from './eventTour'
import { brand } from '@shared/brand'

const READ = 'sales.tour.read'
const WRITE = 'sales.tour.write'
const REPORT = 'sales.tour.report'

const zInstant = z.iso.datetime({ offset: true, error: 'Bitte einen Zeitpunkt nach ISO 8601 angeben.' })
const zText = (max: number) => z.string().trim().max(max)
const zLat = z.number({ error: 'Bitte einen Breitengrad angeben.' }).min(-90).max(90)
const zLng = z.number({ error: 'Bitte einen Längengrad angeben.' }).min(-180).max(180)

// ---- Gemeinsame Formen --------------------------------------------------------------------------------------------

export const zTourVehicle = z.object({
  id: zId,
  code: z.string(),
  name: z.string(),
  plate: z.string().nullable(),
  /** Kasten-Äquivalente: custom_fields.capacityCrates, sonst capacityPallets × 40, sonst 60 (T6) */
  capacityCrates: z.number(),
})

export const zTourDriver = z.object({ userId: zId, name: z.string(), email: z.string().nullable() })

export const zTourWarning = z.object({
  code: z.enum(TOUR_WARNING_CODES),
  message: z.string(),
  severity: z.enum(['info', 'warning']),
  /** der betroffene Stopp; null = die ganze Tour */
  stopId: zId.nullable(),
})

export const zTourRoute = z.object({
  distanceM: z.int(),
  durationS: z.int(),
  /**
   * Herkunft von Linienzug und Strecke: osrm = Straßenrouting; fallback = Luftlinie × 1,3 bei 30 km/h – die Oberfläche zeigt
   * dann „Fahrzeiten geschätzt“, sofern nicht `traffic` gilt. Bleibt bei genau diesen zwei Werten (die Bestell-App prüft sie).
   */
  source: z.enum(['osrm', 'fallback']),
  /** legs[0] = Depot → Stopp 1 … legs[n-1] = Stopp n-1 → Stopp n, legs[n] = Rückfahrt zum Depot (n+1 Abschnitte; ohne Lage: Länge 0, leerer Linienzug) */
  legs: z.array(z.object({ distanceM: z.int(), durationS: z.int(), polyline6: z.string() })),
  // ---- additiv seit 20.10.2026: Fahrzeiten mit Verkehr über HERE (docs/disposition/HERE.md) ----
  /** true = Fahrzeiten (und damit die geplanten Ankunftszeiten) mit Verkehr von HERE; Linienzug und Strecke bleiben vom eigenen Routendienst */
  traffic: z.boolean().default(false),
  /**
   * Ende der Speicherfrist der Werte von HERE in dieser Tour (Fahrzeiten, auch Ankunftszeiten schon angefahrener Stopps) –
   * danach rechnet Ordaneo ohne HERE neu (wer sie speichert, löscht sie bis dahin); null ohne Werte von HERE
   */
  trafficUntil: z.date().nullable().default(null),
  /** Pflichthinweis, wo Zeiten von HERE erscheinen: „Fahrzeiten mit Verkehr: © 2026 HERE“; null ohne Werte von HERE */
  attribution: z.string().nullable().default(null),
  /** warum die Tour ohne Verkehr gerechnet ist, obwohl HERE an ist (Kontingent, Ausfall …); sonst null */
  trafficNote: z.string().nullable().default(null),
})

export const zTourAddress = z.object({
  name: z.string().nullable(),
  street: z.string().nullable(),
  zip: z.string().nullable(),
  city: z.string().nullable(),
  note: z.string().nullable(),
  phone: z.string().nullable(),
})

/** Auftrag eines Stopps bzw. planbarer Auftrag: das, was die Disposition über ihn wissen muss. */
const zOrderFacts = {
  orderId: zId,
  orderNumber: z.string().nullable(),
  /** Nummer in der Bestell-App („AL-24801“) */
  externalNumber: z.string().nullable(),
  /** Kennung der Bestellung der App; null = Auftrag aus Merxo */
  externalOrderId: z.string().nullable(),
  orderStatus: z.string(),
  orderStatusLabel: z.string(),
  customer: z.object({ id: zId.nullable(), number: z.string().nullable(), name: z.string() }),
  address: zTourAddress.nullable(),
  /** Anschrift in einer Zeile */
  addressText: z.string().nullable(),
  /** null = keine Lage ermittelbar (die Oberfläche bietet „Lage korrigieren“) */
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  locationSource: z.enum(GEOCODE_SOURCES).nullable(),
  timeFrom: z.string().nullable(),
  timeTo: z.string().nullable(),
  /** „08:00–12:00 Uhr“ */
  windowText: z.string().nullable(),
  loadCrates: z.number(),
  grossCents: z.int(),
  channelLabel: z.string().nullable(),
  driverNote: z.string().nullable(),
  /** additiv (Veranstaltungen): delivery = Lieferung eines Auftrags, event_pickup = Abholung bei einer Veranstaltung (`orderId` ist dann der Abholauftrag AH-…); fehlt = Lieferung */
  stopKind: z.enum(STOP_KINDS).optional(),
}

export const zTourStop = z.object({
  stopId: zId,
  seq: z.int(),
  status: z.enum(STOP_STATUSES),
  plannedEta: z.date().nullable(),
  arrivedAt: z.date().nullable(),
  doneAt: z.date().nullable(),
  /** geplante Ankunft nach dem Ende des Zeitfensters */
  late: z.boolean(),
  ...zOrderFacts,
})

export const zTour = z.object({
  id: zId,
  tourDate: z.string(),
  name: z.string(),
  status: z.enum(TOUR_STATUSES),
  statusLabel: z.string(),
  /** zählt bei jeder Änderung hoch (die App ignoriert ältere Fassungen) */
  version: z.int(),
  plannedStart: z.date(),
  startedAt: z.date().nullable(),
  finishedAt: z.date().nullable(),
  vehicle: zTourVehicle,
  driver: zTourDriver.nullable(),
  /** Ladung aller Stopps in Kasten-Äquivalenten und Auslastung in Prozent der Kapazität */
  loadCrates: z.number(),
  utilizationPct: z.int(),
  /** voraussichtliches Ende am Depot (Rückfahrt eingerechnet); null ohne Stopps */
  plannedEnd: z.date().nullable(),
  route: zTourRoute.nullable(),
  warnings: z.array(zTourWarning),
  stops: z.array(zTourStop),
  createdAt: z.date(),
  updatedAt: z.date(),
})

export const zPlannableOrder = z.object({
  ...zOrderFacts,
  /** Hinweise zur Lage: keine oder ungefähre */
  warnings: z.array(zTourWarning),
})

export const zDispatchSettings = z.object({
  depotLocationId: zId.nullable(),
  depotName: z.string().nullable(),
  depot: z.object({ lat: z.number(), lng: z.number(), approx: z.boolean() }).nullable(),
  /** Verweildauer je Stopp in Minuten (Vorgabe 4) */
  dwellMinutes: z.int(),
  /** Beginn einer neuen Tour 'HH:MM' (Vorgabe 08:00) */
  defaultStart: z.string(),
  /** höchste Stoppzahl je Tour; null = unbegrenzt */
  maxStops: z.int().nullable(),
  /** Fahrzeiten mit Verkehr über HERE für diesen Betrieb (wirkt nur, wenn HERE auf dem Server eingerichtet ist) */
  hereTraffic: z.boolean().default(false),
})

const zDispatchVehicle = zTourVehicle.extend({ driver: zTourDriver.nullable() })

// ---- Tafel, Lesen --------------------------------------------------------------------------------------------------

export const tourBoard = defineContract({
  name: 'sales.tour.board',
  title: 'Disposition eines Tages anzeigen',
  description: 'Planbare Aufträge ohne Tour, die Touren des Tages mit Stopps, Auslastung und Warnungen, Fahrzeuge und Fahrer.',
  llm: {
    description:
      'Liefert die Tafel der Disposition für einen Tag (Standard: heute): planbare Lieferaufträge ohne Tour (bestätigt oder ' +
      'teilgeliefert, Liefertermin = Tag, keine Abholung) mit Lage, Zeitfenster, Ladung (Gebinde-Äquivalente) und Hinweisen, die Touren ' +
      'des Tages mit Stopps in Reihenfolge, geplanten Ankunftszeiten, Route, Auslastung und Warnungen (Überladung, ' +
      'Zeitfenster, Lage), dazu Fahrzeuge, Fahrer und Einstellungen. Ändert nichts – geplant wird mit ' +
      'sales.tour.plan, angelegt mit sales.tour.create. Ein Fahrer sieht nur Touren seines Fahrzeugs.',
    keywords: ['Disposition', 'Tourenplanung', 'Tour', 'Touren heute', 'Fahrzeuge', 'Fahrer zuweisen', 'wer fährt wohin'],
  },
  input: z.strictObject({ date: zDay.optional() }),
  output: z.object({
    date: z.string(),
    today: z.string(),
    depot: z.object({ locationId: zId, name: z.string(), lat: z.number(), lng: z.number(), approx: z.boolean() }).nullable(),
    orders: z.array(zPlannableOrder),
    tours: z.array(zTour),
    vehicles: z.array(zDispatchVehicle.extend({ busy: z.boolean() })),
    drivers: z.array(zTourDriver),
    settings: zDispatchSettings,
    /** Anschriften des Tages ohne gespeicherte Lage (die Oberfläche kann sie mit sales.tour.geocode ermitteln lassen) */
    unlocated: z.int(),
  }),
  permission: READ,
  risk: 'read',
  scope: 'any',
})

export const tourGet = defineContract({
  name: 'sales.tour.get',
  title: 'Tour anzeigen',
  description: 'Eine Tour mit Stopps, Route, Auslastung und Warnungen.',
  llm: {
    description: 'Liefert eine Tour (Id) mit Stopps in Reihenfolge, geplanten Ankunftszeiten, Route, Auslastung und Warnungen.',
    keywords: ['Tour', 'Stopps', 'Route', 'Ankunftszeit'],
  },
  input: z.strictObject({ id: zId }),
  output: zTour,
  permission: READ,
  risk: 'read',
  scope: 'record',
})

export const tourList = defineContract({
  name: 'sales.tour.list',
  title: 'Touren eines Zeitraums anzeigen',
  description: 'Touren von … bis … (höchstens 31 Tage), wahlweise nach Zustand.',
  llm: {
    description: 'Listet die Touren eines Zeitraums (höchstens 31 Tage) mit Stopps; `status` schränkt auf Zustände ein. Ein Fahrer sieht nur Touren seines Fahrzeugs.',
    keywords: ['Touren', 'Tourenliste', 'Touren der Woche'],
  },
  input: z.strictObject({ from: zDay, to: zDay, status: z.array(z.enum(TOUR_STATUSES)).max(5).optional(), vehicleLocationId: zId.optional() }),
  output: z.object({ tours: z.array(zTour) }),
  permission: READ,
  risk: 'read',
  scope: 'any',
})

// ---- Anlegen und ändern -------------------------------------------------------------------------------------------

const startFields = {
  /** Beginn als Uhrzeit am Tourtag 'HH:MM' (Berlin) – oder als Zeitpunkt; Standard: Einstellung der Disposition */
  startTime: zTime.optional(),
  plannedStart: zInstant.optional(),
}

export const tourCreate = defineContract({
  name: 'sales.tour.create',
  title: 'Tour anlegen',
  description: 'Legt eine Tour (Entwurf) für einen Tag und ein Fahrzeug an – optional mit Fahrer, Beginn und Aufträgen.',
  llm: {
    description:
      'Legt eine Tour im Zustand „Geplant“ an: Tag, Fahrzeug (Standort der Art Fahrzeug), optional Fahrer, Beginn und eine ' +
      'Liste planbarer Aufträge als Stopps. Der Name entsteht aus der Zählung des Tages („Tour 2 · FZ-1“). Gesehen wird ' +
      'die Tour in der App erst nach sales.tour.release.',
    keywords: ['Tour anlegen', 'Tour planen', 'neue Tour', 'Fahrzeug einplanen'],
  },
  input: z.strictObject({
    date: zDay,
    vehicleLocationId: zId,
    driverUserId: zId.nullish(),
    name: zText(80).optional(),
    ...startFields,
    orderIds: z.array(zId).max(200).optional(),
  }),
  output: zTour,
  permission: WRITE,
  risk: 'write',
  assistant: 'opt-in',
  scope: (input) => [input.vehicleLocationId],
  effects: { reversible: false },
})

export const tourUpdate = defineContract({
  name: 'sales.tour.update',
  title: 'Tour ändern',
  description: 'Ändert Name, Fahrer, Beginn und – vor dem Start – das Fahrzeug einer Tour.',
  llm: {
    description:
      'Ändert eine geplante oder freigegebene Tour: Name, Fahrer (`null` entfernt ihn), Beginn (Uhrzeit oder Zeitpunkt) und das ' +
      'Fahrzeug – nur solange die Tour nicht unterwegs ist. Die Ankunftszeiten werden neu gerechnet; bei freigegebenen ' +
      'Touren erfährt die App davon.',
    keywords: ['Fahrer zuweisen', 'Beginn ändern', 'Fahrzeug wechseln', 'Tour umbenennen'],
  },
  input: z.strictObject({
    id: zId,
    name: zText(80).optional(),
    driverUserId: zId.nullish(),
    ...startFields,
    vehicleLocationId: zId.optional(),
  }),
  output: zTour,
  permission: WRITE,
  risk: 'write',
  assistant: 'opt-in',
  scope: 'record',
  effects: { reversible: true },
})

export const tourSetStops = defineContract({
  name: 'sales.tour.setStops',
  title: 'Stopps einer Tour festlegen',
  description: 'Setzt die Stoppfolge einer Tour als Liste von Auftrags-Ids; Aufträge anderer offener Touren wandern hierher.',
  llm: {
    description:
      'Setzt die vollständige Stoppfolge einer Tour: eine Liste von Auftrags-Ids in der gewünschten Reihenfolge. Fehlende ' +
      'Aufträge werden entfernt, neue angehängt; ein Auftrag einer anderen offenen Tour wird dorthin verschoben und aus der ' +
      'alten entfernt. Angefahrene, zugestellte und gescheiterte Stopps bleiben auf ihrem Platz und müssen in der Liste ' +
      'stehen. Planbar sind bestätigte Lieferaufträge mit Liefertermin am Tourtag. Rechnet Ankunftszeiten und Route neu.',
    keywords: ['Stopps', 'Reihenfolge', 'umordnen', 'Auftrag auf Tour', 'Auftrag verschieben'],
  },
  input: z.strictObject({ id: zId, orderIds: z.array(zId).max(200) }),
  output: z.object({
    tour: zTour,
    /** Aufträge, die von einer anderen offenen Tour hierher gewandert sind */
    moved: z.array(z.object({ orderId: zId, orderNumber: z.string().nullable(), fromTourId: zId, fromTourName: z.string() })),
    /** Aufträge, die aus dieser Tour entfernt wurden (wieder planbar) */
    removed: z.array(zId),
  }),
  permission: WRITE,
  risk: 'write',
  assistant: 'opt-in',
  scope: 'record',
  effects: { reversible: true },
})

export const tourPlan = defineContract({
  name: 'sales.tour.plan',
  title: 'Touren automatisch planen',
  description: 'Verteilt die planbaren Aufträge eines Tages auf die gewählten Fahrzeuge – als Vorschlag oder angewendet.',
  llm: {
    description:
      'Plant einen Tag: verteilt planbare Aufträge (oder die genannten) auf die gewählten Fahrzeuge unter Beachtung von ' +
      'Kapazität (Gebinde-Äquivalente), Zeitfenstern und Verweildauer – mit dem Optimierer (VROOM), sonst mit der Ersatzrechnung. ' +
      '`apply: false` liefert nur den Vorschlag, `apply: true` legt ihn als geplante Touren an bzw. ergänzt bestehende ' +
      'geplante Touren (`keepExisting`). Freigegebene und laufende Touren fasst die Planung nie an. Was in keine Tour passt, ' +
      'steht unter `unassigned`.',
    keywords: ['automatisch planen', 'Touren planen', 'Tourenplanung', 'Vorschlag', 'verteilen', 'optimieren'],
  },
  input: z.strictObject({
    date: zDay,
    vehicleLocationIds: z.array(zId).min(1, { error: 'Bitte mindestens ein Fahrzeug wählen.' }).max(12),
    /** nur diese Aufträge planen; ohne Angabe alle planbaren des Tages */
    orderIds: z.array(zId).max(300).optional(),
    /** Fahrer je Fahrzeug (Fahrzeug-Id → Benutzer-Id); fehlt ein Eintrag, bleibt der Fahrer leer bzw. der bisherige */
    drivers: z.record(zId, zId.nullable()).optional(),
    startTime: zTime.optional(),
    apply: z.boolean().default(false),
    /** bestehende geplante Touren der Fahrzeuge behalten und nur ergänzen (Standard) */
    keepExisting: z.boolean().default(true),
  }),
  output: z.object({
    date: z.string(),
    applied: z.boolean(),
    /** vroom = Optimierer; fallback = Ersatzrechnung */
    planner: z.enum(['vroom', 'fallback']),
    routeSource: z.enum(['osrm', 'fallback']),
    /** Planung mit der Zeitmatrix mit Verkehr von HERE (Ankunftszeiten des Vorschlags mit Verkehr) */
    traffic: z.boolean().default(false),
    attribution: z.string().nullable().default(null),
    /** warum ohne Verkehr geplant wurde, obwohl HERE an ist (Kontingent der Matrix, Ausfall …) */
    trafficNote: z.string().nullable().default(null),
    tours: z.array(
      z.object({
        /** Id der angelegten bzw. ergänzten Tour (nur wenn angewendet) */
        tourId: zId.nullable(),
        vehicle: zTourVehicle,
        driver: zTourDriver.nullable(),
        name: z.string(),
        round: z.int(),
        plannedStart: z.date(),
        plannedEnd: z.date().nullable(),
        loadCrates: z.number(),
        capacityCrates: z.number(),
        distanceM: z.int(),
        durationS: z.int(),
        lateCount: z.int(),
        /** Stopps, die vorher schon in der Tour standen */
        existingStops: z.int(),
        stops: z.array(
          z.object({
            orderId: zId,
            orderNumber: z.string().nullable(),
            customerName: z.string(),
            addressText: z.string().nullable(),
            eta: z.date().nullable(),
            timeFrom: z.string().nullable(),
            timeTo: z.string().nullable(),
            loadCrates: z.number(),
            late: z.boolean(),
            isNew: z.boolean(),
          }),
        ),
      }),
    ),
    unassigned: z.array(
      z.object({ orderId: zId, orderNumber: z.string().nullable(), customerName: z.string(), reason: z.enum(['capacity', 'no_vehicle']), message: z.string() }),
    ),
  }),
  permission: WRITE,
  risk: 'write',
  assistant: 'opt-in',
  scope: (input) => input.vehicleLocationIds,
  effects: { reversible: false },
})

export const tourOptimize = defineContract({
  name: 'sales.tour.optimize',
  title: 'Route einer Tour optimieren',
  description: 'Rechnet die Reihenfolge der noch offenen Stopps neu und übernimmt sie, wenn sie mindestens 2 % kürzer ist oder Zeitfenster-Verletzungen beseitigt.',
  llm: {
    description:
      'Rechnet die Reihenfolge der noch nicht angefahrenen Stopps einer Tour neu (Optimierer, sonst Ersatzrechnung). ' +
      'Übernommen wird sie nur, wenn die Fahrzeit mindestens 2 % kürzer ist oder Zeitfenster-Verletzungen entfallen; ' +
      'sonst bleibt die Tour, wie sie ist (`changed: false`).',
    keywords: ['Route optimieren', 'Reihenfolge optimieren', 'kürzeste Route', 'Tour verbessern'],
  },
  input: z.strictObject({ id: zId }),
  output: z.object({
    changed: z.boolean(),
    message: z.string(),
    before: z.object({ durationS: z.int(), lateCount: z.int() }),
    after: z.object({ durationS: z.int(), lateCount: z.int() }),
    tour: zTour,
  }),
  permission: WRITE,
  risk: 'write',
  assistant: 'opt-in',
  scope: 'record',
  effects: { reversible: true },
})

export const tourRelease = defineContract({
  name: 'sales.tour.release',
  title: 'Tour freigeben',
  description: 'Gibt eine geplante Tour – oder alle geplanten Touren eines Tages – für die App und den Fahrer frei.',
  llm: {
    description:
      'Gibt eine geplante Tour (`id`) oder alle geplanten Touren eines Tages (`date`) frei: Sie sind dann in der Bestell-App ' +
      'und für den Fahrer sichtbar, die App wird benachrichtigt. Leere Touren und Touren ohne Fahrzeug-Fahrer-Wunsch ' +
      'werden trotzdem freigegeben; angefahrene Touren gibt es nicht. Nicht rückgängig – abbrechen mit sales.tour.cancel.',
    keywords: ['Tour freigeben', 'Touren freigeben', 'an die App schicken', 'veröffentlichen'],
  },
  input: z
    .strictObject({ id: zId.optional(), date: zDay.optional() })
    .refine((input) => (input.id === undefined) !== (input.date === undefined), { error: 'Bitte eine Tour oder einen Tag angeben.', path: ['id'] }),
  output: z.object({ tours: z.array(zTour), message: z.string() }),
  permission: WRITE,
  risk: 'write',
  assistant: 'opt-in',
  scope: 'record',
  effects: { external: true },
})

export const tourCancel = defineContract({
  name: 'sales.tour.cancel',
  title: 'Tour abbrechen',
  description: 'Bricht eine geplante oder freigegebene Tour ab; ihre Aufträge sind wieder planbar.',
  llm: {
    description: 'Bricht eine Tour im Zustand „Geplant“ oder „Freigegeben“ ab. Die Aufträge werden wieder planbar; die App erfährt davon. Eine laufende Tour lässt sich nicht abbrechen.',
    keywords: ['Tour abbrechen', 'Tour stornieren', 'Tour löschen'],
  },
  input: z.strictObject({ id: zId, reason: zText(300).optional() }),
  output: zTour,
  permission: WRITE,
  risk: 'write',
  assistant: 'opt-in',
  scope: 'record',
  effects: { reversible: false },
})

export const tourComplete = defineContract({
  name: 'sales.tour.complete',
  title: 'Tour abschließen',
  description: 'Schließt eine laufende Tour von Hand ab (wenn die App ausfällt); offene Stopps werden wieder planbar.',
  llm: {
    description: 'Schließt eine laufende Tour von Hand ab, wenn die App das Ende nicht meldet. Nicht angefahrene Stopps bleiben „offen“, ihre Aufträge sind wieder planbar; die Position des Fahrzeugs entfällt.',
    keywords: ['Tour abschließen', 'Tour beenden', 'Tourende'],
  },
  input: z.strictObject({ id: zId, reason: zText(300).optional() }),
  output: zTour,
  permission: WRITE,
  risk: 'write',
  assistant: 'opt-in',
  scope: 'record',
  effects: { reversible: false },
})

// ---- Live ----------------------------------------------------------------------------------------------------------

export const tourLive = defineContract({
  name: 'sales.tour.live',
  title: 'Fahrzeuge live anzeigen',
  description: 'Positionen, Stoppstände und Ankunftszeiten der laufenden Touren eines Tages.',
  llm: {
    description:
      'Liefert zu den freigegebenen und laufenden Touren eines Tages (Standard: heute) die letzte Position je Fahrzeug (nur ' +
      'während einer aktiven Tour, mit Alter), den Fortschritt, den nächsten Stopp und die Ankunftszeit laut App samt ' +
      'Verspätung gegen den Plan. „Keine Meldung seit 5 min“ steht als Hinweis.',
    keywords: ['Wo ist der Fahrer', 'Live-Karte', 'Fahrzeug Position', 'Verspätung', 'Ankunftszeit'],
  },
  input: z.strictObject({ date: zDay.optional() }),
  output: z.object({
    date: z.string(),
    serverTime: z.date(),
    depot: z.object({ name: z.string(), lat: z.number(), lng: z.number() }).nullable(),
    tours: z.array(
      z.object({
        tour: zTour,
        position: z
          .object({
            lat: z.number(),
            lng: z.number(),
            accuracyM: z.int().nullable(),
            headingDeg: z.int().nullable(),
            speedKmh: z.number().nullable(),
            at: z.date(),
            ageS: z.int(),
            /** seit mehr als 5 Minuten keine Meldung */
            silent: z.boolean(),
          })
          .nullable(),
        progress: z.object({ done: z.int(), failed: z.int(), total: z.int() }),
        /** nächster offener Stopp mit Ankunftszeit laut App (letzte gemeldete Schätzung) und Verspätung in Minuten */
        next: z
          .object({ stopId: zId, seq: z.int(), customerName: z.string(), addressText: z.string().nullable(), plannedEta: z.date().nullable(), appEta: z.date().nullable(), delayMinutes: z.int().nullable() })
          .nullable(),
        /** je Stopp die Ankunftszeit laut App (falls gemeldet) */
        appEtas: z.array(z.object({ stopId: zId, eta: z.date() })),
      }),
    ),
  }),
  permission: READ,
  risk: 'read',
  scope: 'any',
})

// ---- Lage, Einstellungen -------------------------------------------------------------------------------------------

export const tourSetLocation = defineContract({
  name: 'sales.tour.setLocation',
  title: 'Lage einer Anschrift korrigieren',
  description: 'Setzt die Lage der Lieferanschrift eines Auftrags von Hand (Pin ziehen); wirkt für alle Aufträge an dieser Anschrift.',
  llm: {
    description:
      'Setzt Breiten- und Längengrad der Lieferanschrift eines Auftrags von Hand. Die Angabe gilt für die ganze Anschrift ' +
      '(Straße, Postleitzahl, Ort) und wird nie von der Adresssuche überschrieben. Offene Touren mit dieser Anschrift ' +
      'werden neu gerechnet.',
    keywords: ['Lage korrigieren', 'Pin ziehen', 'Adresse verorten', 'Koordinaten'],
  },
  input: z.strictObject({ orderId: zId, lat: zLat, lng: zLng }),
  output: z.object({ orderId: zId, lat: z.number(), lng: z.number(), source: z.enum(GEOCODE_SOURCES), updatedTourIds: z.array(zId) }),
  permission: WRITE,
  risk: 'write',
  assistant: 'opt-in',
  scope: 'record',
  effects: { reversible: true },
})

export const tourGeocode = defineContract({
  name: 'sales.tour.geocode',
  title: 'Anschriften eines Tages verorten',
  description: 'Ermittelt die Lage aller Lieferanschriften planbarer und verplanter Aufträge eines Tages (Adresssuche, sonst Postleitzahl).',
  llm: {
    description: `Ermittelt und speichert die Lage der Lieferanschriften eines Tages: gespeicherte bleiben, fehlende fragt ${brand.name} beim eigenen Adressdienst ab, ersatzweise gilt der Mittelpunkt der Postleitzahl („ungefähr“).`,
    keywords: ['verorten', 'Geocoding', 'Adressen suchen', 'Lage ermitteln'],
  },
  input: z.strictObject({ date: zDay }),
  output: z.object({ located: z.int(), approx: z.int(), missing: z.int() }),
  permission: WRITE,
  risk: 'write',
  assistant: 'opt-in',
  scope: 'record',
  effects: { reversible: false },
})

export const tourSettings = defineContract({
  name: 'sales.tour.settings',
  title: 'Einstellungen der Disposition anzeigen',
  description: 'Depot, Verweildauer, Beginn und höchste Stoppzahl – dazu, ob Adressdienst, Routendienst und Optimierer erreichbar sind.',
  llm: {
    description: 'Liefert die Einstellungen der Disposition (Depot, Verweildauer je Stopp, Beginn, höchste Stoppzahl, Fahrzeiten mit Verkehr) und den Zustand der Dienste (Adresssuche, Routing, Optimierer, HERE: eingerichtet und erreichbar ja/nein, Monatsverbrauch – ohne Adressen und Schlüssel).',
    keywords: ['Einstellungen Disposition', 'Depot', 'Verweildauer', 'Routendienst'],
  },
  input: z.strictObject({}),
  output: zDispatchSettings.extend({
    /** wählbare Depots: Standorte der Art Lager und Markt */
    depotChoices: z.array(z.object({ id: zId, code: z.string(), name: z.string(), kind: z.string() })),
    services: z.object({
      geocoder: z.object({ configured: z.boolean(), reachable: z.boolean().nullable() }),
      routing: z.object({ configured: z.boolean(), reachable: z.boolean().nullable() }),
      optimizer: z.object({ configured: z.boolean(), reachable: z.boolean().nullable() }),
    }),
    /** Fahrzeiten mit Verkehr über HERE – ohne Schlüssel (docs/disposition/HERE.md §11) */
    traffic: z
      .object({
        /** MERXO_HERE_API_KEY auf dem Server gesetzt (und in gültiger Form) */
        configured: z.boolean(),
        /** dieser Betrieb steht in MERXO_HERE_TENANTS und ist kein öffentlicher Demo-Betrieb (nur dann lässt sich HERE einschalten) */
        allowed: z.boolean().default(false),
        /** HERE falsch eingerichtet (Satz ohne Schlüssel) – HERE ist dann aus */
        problem: z.string().nullable().default(null),
        /** HERE hat den Schlüssel abgelehnt (401/403) – bis zum Neustart des Servers ohne Verkehr */
        keyRejected: z.boolean().default(false),
        /** letzter Aufruf seit dem Start: ok, Fehler, null = noch keiner (es gibt keinen Probeaufruf – jeder kostet) */
        reachable: z.boolean().nullable(),
        lastCallAt: z.date().nullable(),
        /** HERE untersagt laut Cache-Control das Speichern – die Zeiten werden nicht verwendet */
        storeBlocked: z.boolean(),
        /** zuletzt gesehene Kopfzeile Cache-Control (Prüfung der Speichererlaubnis) */
        cacheControl: z.string().nullable(),
        /** Verbrauch im laufenden Monat (UTC) in Transaktionen; used null = Zähler beginnt gerade neu */
        routing: z.object({ used: z.int().nullable(), limit: z.int() }),
        matrix: z.object({ used: z.int().nullable(), limit: z.int() }),
        /** in Touren dieses Betriebs stehen noch Zeiten von HERE (der Assistent bekommt dann keine Touren) */
        stored: z.boolean(),
      })
      .default({ configured: false, allowed: false, problem: null, keyRejected: false, reachable: null, lastCallAt: null, storeBlocked: false, cacheControl: null, routing: { used: null, limit: 0 }, matrix: { used: null, limit: 0 }, stored: false }),
  }),
  permission: READ,
  risk: 'read',
  scope: 'any',
})

export const tourSaveSettings = defineContract({
  name: 'sales.tour.saveSettings',
  title: 'Einstellungen der Disposition speichern',
  description: 'Legt Depot, Verweildauer je Stopp, Beginn einer neuen Tour und höchste Stoppzahl fest.',
  llm: {
    description: 'Speichert die Einstellungen der Disposition: Depot (Standort), Verweildauer je Stopp in Minuten, Beginn einer neuen Tour (HH:MM) und höchste Stoppzahl (leer = unbegrenzt). Neue Berechnungen verwenden sie; bestehende Touren ändern sich erst bei der nächsten Änderung.',
    keywords: ['Einstellungen Disposition', 'Depot festlegen', 'Verweildauer'],
  },
  input: z.strictObject({
    depotLocationId: zId.nullish(),
    dwellMinutes: z.int().min(0).max(60).optional(),
    defaultStart: zTime.optional(),
    maxStops: z.int().min(1).max(200).nullish(),
    /** Fahrzeiten mit Verkehr über HERE ein- oder ausschalten (ausschalten entfernt gespeicherte Zeiten von HERE) */
    hereTraffic: z.boolean().optional(),
  }),
  output: tourSettings.output,
  permission: WRITE,
  risk: 'write',
  assistant: 'opt-in',
  scope: () => [],
  effects: { reversible: true },
})

// ---- Ereignisse ----------------------------------------------------------------------------------------------------

export const tourChanged = defineEvent({
  name: 'sales.tour.changed',
  title: 'Tour geändert',
  payload: z.object({ tourId: zId, tourDate: z.string(), status: z.enum(TOUR_STATUSES), version: z.int() }),
})

export const tourPositionChanged = defineEvent({
  name: 'sales.tour.positionChanged',
  title: 'Position eines Fahrzeugs gemeldet',
  payload: z.object({ vehicleLocationId: zId, tourId: zId.nullable() }),
})

/** Webhook „Bestell-App“ (sales.channel.*): nur Auslöser – die App holt die Touren mit sales.channel.tours. */
export const channelTourChanged = defineEvent({
  name: 'sales.channel.tourChanged',
  title: 'Tour für einen Vertriebskanal geändert',
  payload: z.object({ tourId: zId, tourDate: z.string(), version: z.int(), reason: z.enum(['released', 'changed', 'cancelled', 'completed']) }),
})

// ---- Öffentliche Verträge für die App (§18.6) ---------------------------------------------------------------------

export const zChannelOrderSnapshot = z.object({
  orderId: zId,
  number: z.string(),
  /**
   * Stand des Auftrags für den Fahrer (additiv, Prüfung 16.10.2026): open = zu übergeben (auch kommissioniert auf dem
   * Fahrzeug), cancelled = in Merxo storniert bzw. Rest geschlossen ohne Lieferung – nicht übergeben, delivered = in Merxo
   * schon vollständig übergeben. Ändert sich der Stand eines Auftrags auf einer freigegebenen oder laufenden Tour, kommt
   * sales.channel.tourChanged (auch wenn der Stopp schon angefahren ist und deshalb bleibt).
   */
  status: z.enum(['open', 'cancelled', 'delivered']),
  deliveryDate: z.string(),
  customer: z.object({ number: z.string().nullable(), name: z.string(), phone: z.string().nullable(), type: z.enum(['private', 'business']) }),
  address: z.object({
    name: z.string(),
    street: z.string(),
    zip: z.string(),
    city: z.string(),
    note: z.string().nullable(),
    lat: z.number(),
    lng: z.number(),
    approx: z.boolean(),
  }),
  lines: z.array(
    z.object({
      sku: z.string(),
      appId: z.string().nullable(),
      name: z.string(),
      unitLabel: z.string(),
      /** Menge im gelieferten Gebinde in Tausendstel (3 Kästen = 3000) */
      qty: z.int(),
      depositCents: z.int().nullable(),
      minAge: z.int().nullable(),
      rental: z.boolean(),
    }),
  ),
  /** an der Tür zu kassieren (0 bei Rechnung/Lastschrift) */
  amountDueCents: z.int(),
  paymentMethod: z.enum(['cash', 'ec', 'invoice', 'sepa', 'prepaid']),
  announcedEmpties: z.array(z.object({ label: z.string(), qty: z.int() })),
  note: z.string().nullable(),
  driverNote: z.string().nullable(),
  reference: z.string().nullable(),
  /** Lieferart des Auftrags (§20.3 S2) */
  fulfillment: z.enum(['delivery', 'pickup']),
  /** Abholcode bei Abholung, damit die Kundschaft ihn in der App sieht; sonst null */
  pickupCode: z.string().nullable(),
  // ---- additiv (Prüfung Markt MR8, 17.10.2026) ----
  /** Lieferfenster bzw. Abholzeitraum des Auftrags (`HH:MM`); null = ohne Fenster. Für Aufträge ohne Tour die einzige Quelle */
  timeFrom: z.string().nullable(),
  timeTo: z.string().nullable(),
  /**
   * Gebühren und Rabatte des Auftrags, je Gebühr eine Zeile (über die Steuersätze zusammengefasst): `delivery` Liefergebühr, `carry`
   * Tragservice, `coupon` Gutschein, `other` von Hand gesetzt. Brutto in Cent mit dem Vorzeichen des Belegs – Gebühren positiv, Rabatte
   * (auch der Gutschein) negativ (anders als `submitOrder.charges`, wo die App den Gutschein positiv meldet).
   */
  charges: z.array(z.object({ code: z.enum(['delivery', 'carry', 'coupon', 'other']), label: z.string(), grossCents: z.int() })),
})

export const zChannelTour = z.object({
  tourId: zId,
  version: z.int(),
  tourDate: z.string(),
  name: z.string(),
  status: z.enum(['released', 'active', 'completed', 'cancelled']),
  vehicle: z.object({ code: z.string(), name: z.string(), plate: z.string().nullable(), capacityCrates: z.number() }),
  driver: z.object({ userId: zId, name: z.string(), email: z.string().nullable() }).nullable(),
  plannedStart: z.date(),
  depot: z.object({ name: z.string(), lat: z.number(), lng: z.number() }),
  route: zTourRoute.nullable(),
  stops: z.array(
    z.object({
      stopId: zId,
      seq: z.int(),
      status: z.enum(STOP_STATUSES),
      plannedEta: z.date().nullable(),
      timeFrom: z.string().nullable(),
      timeTo: z.string().nullable(),
      loadCrates: z.number(),
      externalOrderId: z.string().nullable(),
      /** immer; bei bekannter externalOrderId nutzt die App ihre eigene Bestellung, sonst bildet sie den Stopp aus dem Abbild */
      order: zChannelOrderSnapshot.nullable(),
      // ---- additiv (Veranstaltungen, docs/integration/bestell-app.md „Veranstaltungen“): nur, wenn der Betrieb die Anbindung
      // eingeschaltet hat – sonst kommen Stopps von Veranstaltungen gar nicht vor ----
      /** delivery = Lieferung, event_pickup = Abholung bei einer Veranstaltung; fehlt = Lieferung */
      stopKind: z.enum(STOP_KINDS).optional(),
      /** Veranstaltung am Stopp (Lieferung oder Abholung); fehlt bzw. null = kein Stopp einer Veranstaltung */
      event: zChannelEventStop.nullable().optional(),
    }),
  ),
})

export const channelTours = defineContract({
  name: 'sales.channel.tours',
  title: 'Touren für einen Vertriebskanal abrufen',
  description: 'Freigegebene und laufende Touren (und Abschlüsse der letzten 24 Stunden) für die Fahrer-App – mit Stopps, Route und Auftragsabbild.',
  llm: {
    description:
      'Schnittstelle der Bestell-App (öffentliche API). Liefert die Touren von … bis … (höchstens 7 Tage) im Zustand ' +
      'freigegeben oder aktiv sowie abgeschlossene und abgebrochene der letzten 24 Stunden: Fahrzeug, Fahrer, Beginn, Depot, ' +
      'Route mit Abschnitten (legs[0] = Depot → Stopp 1, letzter Abschnitt = Rückfahrt) und Stopps mit geplanter Ankunft, Zeitfenster, Ladung und – für ' +
      'jeden Stopp – dem vollständigen Auftragsabbild (auch bei Bestellungen der App). `serverTime` ist der Zeitpunkt der Antwort.',
    keywords: ['Touren', 'Fahrer-App', 'Bestell-App', 'Schnittstelle', 'Abgleich'],
  },
  input: z
    .strictObject({ from: zDay, to: zDay })
    .refine((input) => input.to >= input.from, { error: 'Das Ende liegt vor dem Beginn.', path: ['to'] })
    .refine((input) => (Date.parse(`${input.to}T00:00:00Z`) - Date.parse(`${input.from}T00:00:00Z`)) / 86_400_000 <= 6, { error: 'Höchstens 7 Tage auf einmal.', path: ['to'] }),
  output: z.object({ tours: z.array(zChannelTour), serverTime: z.date() }),
  permission: READ,
  risk: 'read',
  scope: 'any',
  publicApi: true,
  assistant: 'never',
})

export const CHANNEL_TOUR_EVENT_KINDS = ['started', 'arrived', 'finished'] as const

export const channelReportTour = defineContract({
  name: 'sales.channel.reportTour',
  title: 'Start, Ankunft oder Ende einer Tour melden',
  description: 'Die Fahrer-App meldet Start, Ankunft an einem Stopp oder das Ende einer Tour.',
  llm: {
    description:
      'Schnittstelle der Bestell-App (öffentliche API). Meldet `started` (freigegeben → aktiv; gebuchte Lieferscheine gehen auf ' +
      '„Unterwegs“), `arrived` (Stopp wird „angekommen“, `stopId` Pflicht) oder `finished` (aktiv → abgeschlossen; offene ' +
      'Stopps werden wieder planbar, die Position entfällt). Idempotent über `eventId`. Passt der Zustand nicht, ' +
      'antwortet der Vertrag mit 409 und `details.reason = tour_state`.',
    keywords: ['Tourstart', 'Ankunft', 'Tourende', 'Fahrer-App', 'Bestell-App', 'Schnittstelle'],
  },
  input: z.strictObject({
    eventId: zText(64).min(8, { error: 'Die Kennung der Meldung ist zu kurz.' }),
    tourId: zId,
    kind: z.enum(CHANNEL_TOUR_EVENT_KINDS),
    at: zInstant,
    stopId: zId.optional(),
    driverName: zText(120).optional(),
  }),
  output: z.object({ tour: z.object({ tourId: zId, status: z.enum(TOUR_STATUSES), version: z.int() }) }),
  permission: REPORT,
  risk: 'write',
  scope: () => [],
  publicApi: true,
  assistant: 'never',
  effects: { reversible: false },
})

export const channelReportPositions = defineContract({
  name: 'sales.channel.reportPositions',
  title: 'Positionen von Fahrzeugen melden',
  description: 'Die Fahrer-App meldet die letzte Position je Fahrzeug während einer aktiven Tour (höchstens alle 15 Sekunden).',
  llm: {
    description:
      'Schnittstelle der Bestell-App (öffentliche API). Meldet 1 bis 10 Positionen: Fahrzeugcode, Tour, Lage, optional Genauigkeit, ' +
      'Richtung, Geschwindigkeit, Zeitpunkt und die geschätzten Ankunftszeiten der nächsten Stopps. Gespeichert wird nur die ' +
      'letzte Position je Fahrzeug und nur für aktive Touren; ältere Meldungen und fremde Touren werden still übergangen. ' +
      'Höchstens 12 Aufrufe je Minute und Schlüssel (sonst 429).',
    keywords: ['Position', 'Live-Ortung', 'Fahrzeug', 'Fahrer-App', 'Bestell-App', 'Schnittstelle'],
  },
  input: z.strictObject({
    positions: z
      .array(
        z.strictObject({
          vehicleCode: zText(20).min(1),
          tourId: zId,
          lat: zLat,
          lng: zLng,
          accuracyM: z.number().min(0).max(100_000).optional(),
          headingDeg: z.number().min(0).max(360).optional(),
          speedKmh: z.number().min(0).max(400).optional(),
          at: zInstant,
          etas: z.array(z.strictObject({ stopId: zId, eta: zInstant })).max(30).optional(),
        }),
      )
      .min(1, { error: 'Bitte mindestens eine Position melden.' })
      .max(10),
  }),
  output: z.object({ accepted: z.int() }),
  permission: REPORT,
  risk: 'write',
  scope: () => [],
  publicApi: true,
  assistant: 'never',
  effects: { reversible: false },
  limits: { maxItems: 10, itemCount: (input) => input.positions.length },
})
